import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Stack, Typography, Paper, Button, TextField,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { apiGet, ApiError } from "../api/client";
import { useToast } from "../ui/Toaster";

// --- formatting helpers ---
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const moneyNeg = (n) => {
  const v = Number(n || 0);
  return v ? `(${money(Math.abs(v))})` : money(0);  // accounting-style parens for negatives
};
const fmtDate = (iso) => {
  // "2026-05-20" → "May 20, 2026"
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
};
const fmtMonth = (ym) => {
  try {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
  } catch { return ym; }
};
function todayISO() { return new Date().toISOString().slice(0, 10); }
function yearStartISO() { return `${new Date().getFullYear()}-01-01`; }

// --- expense-type label normalization ---
// The DB stores raw machine-readable values like "ebay_promotion_fee" or
// "Storage". For a document going to a mortgage underwriter, those need
// to read like accountant-prepared categories ("eBay Promoted Listings
// Fees", "Warehouse Rent / Storage"). This map covers every type the
// codebase emits; the fallback humanizer handles anything new.
const TYPE_LABELS = {
  ebay_promotion_fee:      "eBay Promoted Listings Fees",
  ebay_store_subscription: "eBay Store Subscription",
  ebay_insertion_fee:      "eBay Insertion Fees",
  ebay_fvf:                "eBay Final Value Fee Adjustments",
  ebay_dispute:            "eBay Disputes",
  ebay_dispute_fee:        "eBay Dispute Fees",
  ebay_adjustment:         "eBay Account Adjustments",
  Ebay:                    "eBay (Miscellaneous)",
  ebay:                    "eBay (Miscellaneous)",
  Storage:                 "Warehouse Rent / Storage",
  Gas:                     "Vehicle Fuel",
  Vehicle:                 "Vehicle Expenses",
  Employee:                "Employee Wages",
  Equipment:               "Equipment",
  Other:                   "Other Expenses",
};
function normalizeTypeLabel(t) {
  if (TYPE_LABELS[t]) return TYPE_LABELS[t];
  if (!t) return "Other";
  // Fallback: turn "snake_case" into "Title Case", then fix common acronyms.
  let s = String(t).replace(/_/g, " ").replace(/\s+/g, " ").trim();
  s = s.replace(/\b\w/g, (c) => c.toUpperCase());
  s = s.replace(/\bEbay\b/g, "eBay").replace(/\bFvf\b/g, "FVF").replace(/\bUsps\b/g, "USPS");
  return s;
}
function normalizePlatform(p) {
  const s = String(p || "").toLowerCase();
  if (s === "ebay") return "eBay";
  if (s === "amazon") return "Amazon";
  if (s === "facebook" || s === "fb") return "Facebook Marketplace";
  if (s === "etsy") return "Etsy";
  return s.replace(/\b\w/g, (c) => c.toUpperCase()) || "Other";
}

// --- print CSS injected when this page mounts ---
// Hides every part of the admin shell so the printed PDF shows ONLY
// the statement. Restores the layout when the page unmounts. The body
// class is the toggle so other pages aren't accidentally affected.
const PRINT_STYLE_ID = "pnl-print-style";
const PRINT_CSS = `
@media print {
  /* Hide everything by default in print, then re-show only the statement. */
  body > *:not(#root) { display: none !important; }
  /* Hide the admin shell — sidebar, topbar, toolbar, command palette,
     toasts, anything that isn't the statement itself. */
  aside, header, nav, [role="banner"], [role="navigation"],
  [data-pnl-toolbar], [data-pnl-no-print] { display: none !important; }
  /* Strip the main-content padding + the margin-left that accommodates
     the sidebar; we want the statement edge-to-edge on the page. */
  main { padding: 0 !important; }
  [data-collapse-aware] { margin-left: 0 !important; }
  /* Neutralize backgrounds + outlines that look fine on screen but
     waste toner. */
  html, body, #root, main { background: #fff !important; color: #111 !important; }
  /* Encourage the browser to keep section headers + their first row
     on the same page. */
  [data-pnl-section] { break-inside: avoid; page-break-inside: avoid; }
  /* Don't break inside the headline net-profit row. */
  [data-pnl-headline] { break-inside: avoid; }
  /* Drop any colored profit/loss text — accounting docs are black ink. */
  [data-pnl-statement] * { color: #111 !important; }
  /* Slightly smaller default for print so a long monthly table fits. */
  [data-pnl-statement] { font-size: 11pt; }
  /* Comfortable letter-size margins. */
  @page { margin: 0.6in; }
}
`;

function usePrintStyles() {
  useEffect(() => {
    if (document.getElementById(PRINT_STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = PRINT_STYLE_ID;
    el.textContent = PRINT_CSS;
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, []);
}

// --- business info, persisted to localStorage so the operator doesn't
// re-type address/EIN every time they print. Lender will want LLC name,
// EIN, business address, and the preparer's name on the statement.
const BIZ_INFO_KEY = "bidrl_pnl_business_info";
const DEFAULT_BIZ_INFO = {
  llc_name:    "Online Retailer VA LLC",
  business_address: "",
  ein:         "",
  preparer:    "Jacob Pittenger, Owner",
};
function loadBizInfo() {
  try {
    const raw = localStorage.getItem(BIZ_INFO_KEY);
    if (!raw) return DEFAULT_BIZ_INFO;
    return { ...DEFAULT_BIZ_INFO, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_BIZ_INFO;
  }
}
function saveBizInfo(info) {
  try { localStorage.setItem(BIZ_INFO_KEY, JSON.stringify(info)); }
  catch { /* ignore quota / SSR */ }
}

export default function PnL() {
  const { error: toastError } = useToast();
  const [from, setFrom] = useState(yearStartISO());
  const [to, setTo] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bizInfo, setBizInfo] = useState(loadBizInfo);
  usePrintStyles();

  const updateBiz = (field, value) => {
    const next = { ...bizInfo, [field]: value };
    setBizInfo(next);
    saveBizInfo(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet(`/api/pnl?from=${from}&to=${to}`);
      setReport(r);
    } catch (e) {
      toastError("Couldn't load P&L", e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [from, to, toastError]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    window.location.href = `/api/pnl?from=${from}&to=${to}&format=csv`;
  };

  if (!report && loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (!report) return null;

  const exp = report.operating_expenses || { total: 0, by_type: [] };

  // Period length in months → monthly average for the headline box.
  // Lenders often eyeball "monthly net" first; showing it saves them the
  // division. We compute days inclusive and convert to months using
  // 30.4375 (365.25/12) so partial periods aren't over- or under-stated.
  // We deliberately do NOT publish an annualized run-rate: underwriters
  // prefer to do their own income projection and can read it as the
  // applicant trying to inflate income.
  const periodDays = (() => {
    try {
      const f = new Date(report.period.from + "T00:00:00");
      const t = new Date(report.period.to   + "T00:00:00");
      return Math.max(1, Math.round((t - f) / 86400000) + 1);
    } catch { return 0; }
  })();
  const periodMonths = periodDays > 0 ? periodDays / 30.4375 : 0;
  const monthlyNet   = periodMonths > 0 ? report.net_profit / periodMonths : 0;

  return (
    <Box>
      {/* On-screen toolbar — completely hidden when printing. */}
      <Stack
        data-pnl-toolbar
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "flex-end" }}
        sx={{ mb: 3 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h1" sx={{ fontSize: { xs: "1.75rem", md: "2.25rem" }, mb: 0.5 }}>
            Profit / Loss Statement
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            Use the date range below to scope the report, then click Print / PDF to save a
            lender-ready copy. Everything except the statement is removed when printing.
          </Typography>
        </Box>
        <TextField label="From" type="date" size="small"
          value={from} onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <TextField label="To" type="date" size="small"
          value={to} onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <Button variant="outlined"
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshOutlinedIcon />}
          onClick={load} disabled={loading}>Reload</Button>
        <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={downloadCsv}>
          CSV
        </Button>
        <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>
          Print / PDF
        </Button>
      </Stack>

      {/* Business info — editable on screen, fixed values render on the
          printed statement. Stored to localStorage so the operator
          doesn't re-type the EIN / address every time they print.
          Collapsed by default; auto-expand if anything required is blank
          so the user sees what's missing. */}
      <Accordion
        data-pnl-toolbar
        defaultExpanded={!bizInfo.business_address || !bizInfo.ein}
        sx={{ mb: 3, "&:before": { display: "none" } }}
        variant="outlined"
      >
        <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Business info on statement</Typography>
          <Typography sx={{ ml: 2, color: "text.secondary", fontSize: 13 }}>
            {bizInfo.llc_name}
            {bizInfo.ein ? ` · EIN ${bizInfo.ein}` : " · EIN missing"}
            {bizInfo.business_address ? "" : " · address missing"}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField label="Legal entity (LLC name)" size="small" fullWidth
              value={bizInfo.llc_name}
              onChange={(e) => updateBiz("llc_name", e.target.value)} />
            <TextField label="EIN" size="small" fullWidth
              placeholder="XX-XXXXXXX"
              value={bizInfo.ein}
              onChange={(e) => updateBiz("ein", e.target.value)} />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 1.5 }}>
            <TextField label="Business address" size="small" fullWidth multiline minRows={2}
              placeholder="1234 Main St, Suite 100&#10;Virginia Beach, VA 23456"
              value={bizInfo.business_address}
              onChange={(e) => updateBiz("business_address", e.target.value)} />
            <TextField label="Prepared by" size="small" fullWidth
              placeholder="Jacob Pittenger, Owner"
              value={bizInfo.preparer}
              onChange={(e) => updateBiz("preparer", e.target.value)} />
          </Stack>
          <Typography sx={{ mt: 1.5, fontSize: 11, color: "text.secondary" }}>
            Saved to your browser. Updates appear on the printed statement immediately —
            no reload needed.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* The statement itself. data-pnl-statement is the print anchor. */}
      <Paper
        data-pnl-statement
        variant="outlined"
        sx={{
          maxWidth: 820,
          mx: "auto",
          px: { xs: 3, md: 6 },
          py: { xs: 3, md: 5 },
          bgcolor: "background.paper",
          fontFamily: "'Georgia', 'Times New Roman', serif",
          color: "text.primary",
          "@media print": { boxShadow: "none", border: "none", px: 0, py: 0, mx: 0, maxWidth: "100%" },
        }}
      >
        {/* Letterhead. LLC name + address + EIN at the top; statement
            title and period below. The "Unaudited / Management-Prepared"
            tag is included verbatim because lenders specifically look
            for it on internal financial statements (vs. CPA-reviewed). */}
        <Box sx={{ textAlign: "center", mb: 3, pb: 2, borderBottom: 2, borderColor: "text.primary" }}>
          <Typography sx={{
            fontFamily: "inherit", fontSize: 26, fontWeight: 700, letterSpacing: 1,
            textTransform: "uppercase",
          }}>
            {bizInfo.llc_name || "Online Retailer VA LLC"}
          </Typography>
          {bizInfo.business_address && (
            <Typography sx={{
              fontFamily: "inherit", fontSize: 12, color: "text.primary", mt: 0.25,
              whiteSpace: "pre-line",
            }}>
              {bizInfo.business_address}
            </Typography>
          )}
          {bizInfo.ein && (
            <Typography sx={{ fontFamily: "inherit", fontSize: 12, color: "text.primary", mt: 0.25 }}>
              EIN: {bizInfo.ein}
            </Typography>
          )}
          <Typography sx={{
            fontFamily: "inherit", fontSize: 16, fontWeight: 700, mt: 2, letterSpacing: 0.5,
            textTransform: "uppercase",
          }}>
            Profit and Loss Statement
          </Typography>
          <Typography sx={{
            fontFamily: "inherit", fontSize: 12, fontStyle: "italic",
            color: "text.primary", mt: 0.25, letterSpacing: 0.3,
          }}>
            Unaudited · Management-Prepared
          </Typography>
          <Typography sx={{ fontFamily: "inherit", fontSize: 13, color: "text.primary", mt: 0.5 }}>
            For the period {fmtDate(report.period.from)} through {fmtDate(report.period.to)}
          </Typography>
        </Box>

        {/* Headline summary — single boxed row up top so the reader sees
            the bottom line before working through the line items. We also
            surface the monthly average + annualized run-rate so the lender
            doesn't have to do the math; the run-rate is plainly labeled
            "informational only" so it can't be mistaken for an audited
            projection. */}
        <Box data-pnl-headline sx={{
          border: 1, borderColor: "text.primary", p: 2.5, mb: 3,
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1,
        }}>
          <Box>
            <Typography sx={{ fontFamily: "inherit", fontSize: 11, textTransform: "uppercase",
              letterSpacing: 0.7, color: "text.secondary" }}>
              Net profit (after all expenses)
            </Typography>
            <Typography sx={{
              fontFamily: "inherit", fontSize: 24, fontWeight: 700, fontVariantNumeric: "tabular-nums",
            }}>
              {report.net_profit < 0 ? moneyNeg(report.net_profit) : money(report.net_profit)}
            </Typography>
            {periodMonths > 0 && (
              <Typography sx={{ fontFamily: "inherit", fontSize: 11.5, mt: 0.75,
                color: "text.primary" }}>
                Monthly average: <b>{monthlyNet < 0 ? moneyNeg(monthlyNet) : money(monthlyNet)}</b>
                <span style={{ color: "rgba(0,0,0,0.55)" }}>
                  {"  "}({periodMonths.toFixed(1)} months)
                </span>
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontFamily: "inherit", fontSize: 11, color: "text.secondary",
              textTransform: "uppercase", letterSpacing: 0.7 }}>
              Period summary
            </Typography>
            <Typography sx={{ fontFamily: "inherit", fontSize: 13, mt: 0.25 }}>
              <b>{report.sold_items.toLocaleString()}</b> orders fulfilled
            </Typography>
            <Typography sx={{ fontFamily: "inherit", fontSize: 13 }}>
              Gross revenue <b>{money(report.revenue)}</b>
            </Typography>
            <Typography sx={{ fontFamily: "inherit", fontSize: 13 }}>
              Gross profit <b>{money(report.gross_profit)}</b>
            </Typography>
          </Box>
        </Box>

        {/* Income statement */}
        <Section title="Income Statement">
          <Table size="small" sx={statementTableSx}>
            <TableBody>
              <Row label="Gross Revenue" amount={report.revenue} bold />
              <Row label="Less: Cost of goods sold" amount={-report.cogs} indent />
              <Row label="Less: Platform fees (eBay, Amazon, etc.)" amount={-report.platform_fees} indent />
              <Row label="Less: Shipping & postage" amount={-report.shipping} indent />
              <Row label="Less: Refunds & disputes" amount={-report.refunds} indent />
              <Row label="Gross profit" amount={report.gross_profit} bold singleRule />
              <SectionLabel>Operating Expenses</SectionLabel>
              {exp.by_type.length === 0 && (
                <TableRow><TableCell colSpan={2} sx={{ fontStyle: "italic", pl: 4 }}>
                  None recorded in period
                </TableCell></TableRow>
              )}
              {exp.by_type.map((e) => (
                <Row key={e.type} label={normalizeTypeLabel(e.type)} amount={-e.amount} indent />
              ))}
              <Row label="Total operating expenses" amount={-exp.total} bold singleRule />
              <Row label="Net profit / (loss)" amount={report.net_profit} bold doubleRule big />
            </TableBody>
          </Table>
        </Section>

        {/* Inventory snapshot */}
        <Section title="Inventory On Hand (as of period end)">
          <Table size="small" sx={statementTableSx}>
            <TableBody>
              <Row label="Item count" amount={report.inventory_on_hand_end.count} raw />
              <Row label="Cost basis (cash invested)" amount={report.inventory_on_hand_end.cost_basis} />
              <Row label="Estimated retail value" amount={report.inventory_on_hand_end.est_retail_value} />
            </TableBody>
          </Table>
          <Typography sx={{ fontFamily: "inherit", fontSize: 10.5, color: "text.primary",
            mt: 0.75 }}>
            <i>COGS reflects only inventory <b>sold during the period</b>. Inventory on hand
            is excluded from expenses and reported separately as a current asset on this
            statement.</i>
          </Typography>
        </Section>

        {/* Inventory acquired in the period */}
        <Section title="Inventory Purchased During Period">
          <Table size="small" sx={statementTableSx}>
            <TableBody>
              <Row label="Lots acquired" amount={report.purchases_in_window.count} raw />
              <Row label="Total cash invested in new inventory" amount={report.purchases_in_window.spend} />
            </TableBody>
          </Table>
        </Section>

        {/* Monthly breakdown */}
        {report.by_month.length > 0 && (
          <Section title="Monthly Breakdown">
            <Table size="small" sx={statementTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Month</TableCell>
                  <TableCell align="right" sx={thSx}>Orders</TableCell>
                  <TableCell align="right" sx={thSx}>Revenue</TableCell>
                  <TableCell align="right" sx={thSx}>COGS</TableCell>
                  <TableCell align="right" sx={thSx}>Fees</TableCell>
                  <TableCell align="right" sx={thSx}>Shipping</TableCell>
                  <TableCell align="right" sx={thSx}>Refunds</TableCell>
                  <TableCell align="right" sx={thSx}>Gross profit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.by_month.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell sx={{ ...tdSx }}>{fmtMonth(m.month)}</TableCell>
                    <TableCell align="right" sx={tdNumSx}>{m.orders.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={tdNumSx}>{money(m.revenue)}</TableCell>
                    <TableCell align="right" sx={tdNumSx}>{money(m.cogs)}</TableCell>
                    <TableCell align="right" sx={tdNumSx}>{money(m.fees)}</TableCell>
                    <TableCell align="right" sx={tdNumSx}>{money(m.shipping)}</TableCell>
                    <TableCell align="right" sx={tdNumSx}>{money(m.refunds)}</TableCell>
                    <TableCell align="right" sx={{ ...tdNumSx, fontWeight: 600 }}>
                      {m.gross_profit < 0 ? moneyNeg(m.gross_profit) : money(m.gross_profit)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        )}

        {/* Sales by platform */}
        {report.by_platform.length > 0 && (
          <Section title="Revenue by Sales Channel">
            <Table size="small" sx={statementTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Channel</TableCell>
                  <TableCell align="right" sx={thSx}>Orders</TableCell>
                  <TableCell align="right" sx={thSx}>Revenue</TableCell>
                  <TableCell align="right" sx={thSx}>Platform fees</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.by_platform.map((p) => (
                  <TableRow key={p.platform}>
                    <TableCell sx={tdSx}>{normalizePlatform(p.platform)}</TableCell>
                    <TableCell align="right" sx={tdNumSx}>{p.orders.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={tdNumSx}>{money(p.revenue)}</TableCell>
                    <TableCell align="right" sx={tdNumSx}>{money(p.fees)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        )}

        {/* Footer: methodology notes, certification, signature block.
            The certification language is the standard wording lenders look
            for on management-prepared statements, signaling the preparer
            stands behind the numbers. */}
        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: "text.primary" }}>
          <Typography sx={{ fontFamily: "inherit", fontSize: 11, color: "text.primary", mb: 1.5 }}>
            <b>Basis of presentation:</b> This statement is an unaudited, management-prepared
            summary of business activity for the stated period. Revenue and expenses are
            recorded using the company's standard accounting practices. Cost of goods sold
            includes only inventory that was sold during the period; inventory on hand at
            period end is excluded from expenses and disclosed separately as a current
            asset above.
          </Typography>
          <Typography sx={{ fontFamily: "inherit", fontSize: 11, color: "text.primary", mb: 2 }}>
            <b>Certification:</b> I certify that this statement is true and accurate to
            the best of my knowledge and fairly represents the financial activity of
            {" "}{bizInfo.llc_name || "the business"} for the period stated. This
            statement is unaudited and management-prepared; supporting transaction-level
            records are available upon request.
          </Typography>
          {/* Formal sign area. The signature line sits above the printed
              name (so a handwritten signature reads above "Jacob Pittenger,
              Owner"), and the Date line is a separate inline blank rather
              than a stacked column — lenders are used to that layout on
              loan paperwork. */}
          <Box sx={{ mt: 4, maxWidth: 460 }}>
            <Box sx={{ borderBottom: 1, borderColor: "text.primary", height: 36 }} />
            <Typography sx={{ fontFamily: "inherit", fontSize: 11.5,
              color: "text.primary", mt: 0.5, fontWeight: 600 }}>
              {bizInfo.preparer || "Prepared by"}
            </Typography>
            <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ mt: 2 }}>
              <Typography sx={{ fontFamily: "inherit", fontSize: 11.5,
                color: "text.primary", fontWeight: 600 }}>
                Date:
              </Typography>
              <Box sx={{ flex: 1, borderBottom: 1, borderColor: "text.primary", height: 22 }} />
            </Stack>
          </Box>
          <Typography sx={{ fontFamily: "inherit", fontSize: 9, color: "text.secondary",
            mt: 3, textAlign: "right" }}>
            Statement generated {new Date(report.generated_at).toLocaleString("en-US", {
              dateStyle: "long", timeStyle: "short",
            })}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

// --- shared style objects ---
const statementTableSx = {
  fontFamily: "inherit",
  "& td, & th": {
    fontFamily: "inherit",
    fontSize: 12,
    borderColor: "rgba(0,0,0,0.12)",
    py: 0.625,
  },
};
const thSx = {
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  fontSize: 10.5,
  color: "text.secondary",
  borderBottom: 2,
  borderColor: "text.primary",
};
const tdSx = {};
const tdNumSx = {
  fontVariantNumeric: "tabular-nums",
};

function Section({ title, children }) {
  return (
    <Box data-pnl-section sx={{ mb: 3 }}>
      <Typography sx={{
        fontFamily: "inherit", fontSize: 13, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.7,
        borderBottom: 1, borderColor: "text.primary",
        pb: 0.5, mb: 1,
      }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function SectionLabel({ children }) {
  // Sub-header within the income-statement table (e.g. "Operating Expenses")
  return (
    <TableRow>
      <TableCell colSpan={2} sx={{
        pt: 1.5, pb: 0.25,
        fontFamily: "inherit",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "text.primary",
        borderBottom: "none",
      }}>
        {children}
      </TableCell>
    </TableRow>
  );
}

function Row({ label, amount, raw, indent, bold, big, singleRule, doubleRule, color }) {
  let labelStyle = {
    fontFamily: "inherit",
    fontSize: big ? 13 : 12,
    fontWeight: bold ? 700 : 400,
    pl: indent ? 3 : 1.5,
  };
  let valueStyle = {
    fontVariantNumeric: "tabular-nums",
    fontFamily: "inherit",
    fontSize: big ? 13 : 12,
    fontWeight: bold ? 700 : 400,
    whiteSpace: "nowrap",
    color,
  };
  // Accounting-style rule lines above totals.
  let rowSx = {};
  if (singleRule) {
    rowSx = { "& td": { borderTop: 1, borderColor: "text.primary", pt: 1 } };
  } else if (doubleRule) {
    rowSx = {
      "& td": {
        borderTop: 1, borderBottom: 4, borderBottomColor: "text.primary",
        borderTopColor: "text.primary",
        borderBottomStyle: "double",
        pt: 1, pb: 1,
      },
    };
  }
  const display = raw
    ? Number(amount).toLocaleString()
    : (amount < 0 ? moneyNeg(amount) : money(amount));
  return (
    <TableRow sx={rowSx}>
      <TableCell sx={labelStyle}>{label}</TableCell>
      <TableCell align="right" sx={valueStyle}>{display}</TableCell>
    </TableRow>
  );
}
